<template>
  <div class="code-editor-container" ref="editorContainer"></div>
</template>

<script>
/**
 * Editor de código leve usando CodeMirror 6
 * Substitui o Monaco Editor (3MB) por CodeMirror (~100KB)
 * Não trava o browser ao abrir arquivos.
 */
import { EditorView, basicSetup } from 'codemirror'
import { EditorState } from '@codemirror/state'
import { javascript } from '@codemirror/lang-javascript'
import { oneDark } from '@codemirror/theme-one-dark'
import { keymap } from '@codemirror/view'

export default {
  name: 'MonacoEditor',
  props: {
    value: { type: String, default: '' },
    language: { type: String, default: 'queen' },
    theme: { type: String, default: 'vs-dark' },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      view: null,
      updating: false,
    }
  },
  mounted() {
    this.$nextTick(() => this.createEditor())
  },
  beforeUnmount() {
    if (this.view) {
      this.view.destroy()
      this.view = null
    }
  },
  methods: {
    createEditor() {
      const container = this.$refs.editorContainer
      if (!container) return

      const self = this
      const updateListener = EditorView.updateListener.of((update) => {
        if (update.docChanged && !self.updating) {
          const newValue = update.state.doc.toString()
          self.$emit('update:value', newValue)
          self.$emit('change', newValue)
        }
      })

      const state = EditorState.create({
        doc: this.value || '',
        extensions: [
          basicSetup,
          javascript(),
          oneDark,
          updateListener,
          EditorView.editable.of(!this.readOnly),
          EditorState.tabSize.of(2),
          EditorView.theme({
            '&': { height: '100%', fontSize: '14px' },
            '.cm-scroller': { overflow: 'auto' },
            '.cm-content': { fontFamily: "'JetBrains Mono', 'Fira Code', monospace" },
          }),
        ],
      })

      this.view = new EditorView({ state, parent: container })
      this.$emit('editorDidMount', this.view)
    },
  },
  watch: {
    value(newVal) {
      if (!this.view) return
      const current = this.view.state.doc.toString()
      if (newVal !== current) {
        this.updating = true
        this.view.dispatch({
          changes: { from: 0, to: current.length, insert: newVal || '' },
        })
        this.updating = false
      }
    },
    readOnly(newVal) {
      if (!this.view) return
      this.view.dispatch({
        effects: EditorView.editable.reconfigure(EditorView.editable.of(!newVal)),
      })
    },
  },
}
</script>

<style scoped>
.code-editor-container {
  width: 100%;
  height: 100%;
  min-height: 300px;
  overflow: hidden;
  background: #282c34;
}
.code-editor-container .cm-editor {
  height: 100%;
}
</style>
